import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Bell, BellRing, CloudSun, Compass, CreditCard, Image as ImageIcon, Languages, Map as MapIcon, MountainSnow, Route, Share2, Sparkles, TriangleAlert, Wallet, WifiOff } from "lucide-react";
import { BestDayPanel } from "./components/BestDayPanel";
import { BudgetPanel } from "./components/BudgetPanel";
import { SoundtrackPanel } from "./components/SoundtrackPanel";
import { SITE_NAME } from "./lib/about";
import { AboutSection } from "./components/AboutSection";
import { MonthAnswer } from "./components/MonthAnswer";
import { CrowdCalendar } from "./components/CrowdCalendar";
import { PlaceTripPanel } from "./components/PlaceTripPanel";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { lazyChunk } from "./lib/chunk";
import { FeedbackDock } from "./components/FeedbackDock";
import { ExplorePanel } from "./components/ExplorePanel";
import { ConnectionRow } from "./components/ConnectionRow";
import { ControlPanel } from "./components/ControlPanel";
import { CorridorPicker } from "./components/CorridorPicker";
import { CorridorShowcase } from "./components/CorridorShowcase";
import { FjordBackdrop } from "./components/FjordBackdrop";
import { EmergencyCard } from "./components/EmergencyCard";
import { FlagIcon } from "./components/FlagIcon";
import { FoldPanel } from "./components/FoldPanel";
import { Roadbook } from "./components/Roadbook";
import { RouteFinder } from "./components/RouteFinder";
import { TravelServices } from "./components/TravelServices";
import { useScrollReveal } from "./lib/reveal";
import { scrollToAnchor, scrollToForm, scrollToPlan } from "./lib/scroll";
import { ItineraryBoard } from "./components/ItineraryBoard";
import { OptimizationPanel } from "./components/OptimizationPanel";
import { ExtendedPanel } from "./components/ExtendedPanel";
import { OutlookPanel } from "./components/OutlookPanel";
import { PackingPanel } from "./components/PackingPanel";
import { ShareTrip } from "./components/ShareTrip";
import { ALL_PLACE_KINDS, PlaceFilter } from "./components/PlaceFilter";
import { RouteMap } from "./components/RouteMap";
import { FerryPanel } from "./components/FerryPanel";
import { blockingCount, TrafficAlertsPanel } from "./components/TrafficAlertsPanel";
import { StopDrawer } from "./components/StopDrawer";
import { StopHoverCard } from "./components/StopHoverCard";
import { TerrainProfile } from "./components/TerrainProfile";
import { WhyItinerariesBreak } from "./components/WhyItinerariesBreak";
import { ThemeToggle } from "./components/ThemeToggle";
import { fetchCorridors, fetchItinerary, fetchStops } from "./lib/api";
import { formatHours } from "./lib/format";
import {
  contentLang,
  detectLang,
  dictionaries,
  loadDictionary,
  hasTranslatedContent,
  LANGUAGES,
  rememberLang,
} from "./lib/i18n";
import { openInCalendar } from "./lib/ics";
import {
  apply as applyTheme,
  rememberChoice,
  resolve as resolveTheme,
  storedChoice,
  watchSystem,
  type ThemeChoice,
} from "./lib/thememode";
import {
  canNotify,
  CHECK_INTERVAL_MS,
  findChanges,
  notificationsAllowed,
  requestPermission,
  showChanges,
} from "./lib/notify";
import { loadItinerary, prefetchImages, saveItinerary } from "./lib/offline";
import type { PlaceKind } from "./lib/mapdata";
import { SERVICE_MARKS } from "./lib/services";
import { VEHICLE_GLYPHS, vehicleGlyphKeyFor, type VehicleGlyphKey } from "./lib/vehicleGlyphs";
import { TAG_COLOR } from "./lib/theme";
import type {
  CorridorOption,
  Interest,
  Itinerary,
  Origin,
  Stop,
  TravelMode,
  TripForm,
  UiLang,
  VehicleKind,
} from "./lib/types";
import {
  DEFAULT_FORM,
  formFromUrl,
  formToQuery,
  hasSharedForm,
  sharedByName,
  shareUrl,
  syncUrl,
  usableForm,
} from "./lib/url";

/**
 * maplibre-gl er ~250 kB gzipped — mer enn resten av appen til sammen. Den
 * lastes derfor først når kartet faktisk vises, ikke ved oppstart.
 */
/*
 * `lazyChunk` og ikke `lazy`: kodebiten forsvinner ved en utrulling, og en
 * avvist `import()` kastes under render. Det var feilen brukerne meldte som
 * «krasjet og lastet hjemskjermen på nytt» — se lib/chunk.ts for hele kjeden.
 */
const TerrainMap3D = lazyChunk("terrain3d", () => import("./components/TerrainMap3D"));

type MapView = "3d" | "flat";

export default function App() {
  const [lang, setLang] = useState<UiLang>(() => detectLang(window.location.search));
  const [theme, setTheme] = useState<ThemeChoice>(storedChoice);
  /*
   * Temaet som faktisk vises. Holdes adskilt fra valget fordi «system» ikke
   * er et utseende — kartet må vite om det skal tegne lyst eller mørkt, og
   * det svaret kan endre seg uten at brukeren rører noe.
   */
  const [appliedTheme, setAppliedTheme] = useState(() => resolveTheme(storedChoice()));
  const [form, setForm] = useState<TripForm>(() => formFromUrl(window.location.search));
  /*
   * Navnet på den som delte lenka, lest én gang ved oppstart.
   *
   * Leses fra `window.location.search` og ikke fra `form.ownerName`, og det er
   * ikke det samme spørsmålet: skriver brukeren sitt eget navn i feltet, havner
   * det i skjemaet og i URL-en — men det gjør dem ikke til en avsender å
   * opplyse *dem* om. Denne skal bare være satt når turen kom utenfra, og
   * derfor står den fast fra første render. Se `sharedByName` i lib/url.ts.
   */
  const [sharedBy] = useState(() => sharedByName(window.location.search));
  /*
   * Åpnet siden på en delt lenke, og har mottakeren ennå ikke tatt over?
   *
   * Meldt fra bruk: en delt lenke skal vise *bare* den valgte ruta — ikke
   * rutevelgeren, ikke innstillingene, ikke «hvorfor appen finnes». Den som får
   * en tur tilsendt kom for å se turen; de tre panelene over den er spørsmål de
   * ikke har stilt, og de skjøv planen ut av første skjerm.
   *
   * `false` så snart de trykker «endre» — da er dette deres egen økt, og hele
   * planleggeren skal fram. Lest én gang ved oppstart, som `sharedBy`: skriver
   * mottakeren sitt eget navn i feltet havner det i URL-en, og det skal ikke
   * gjøre siden om til en delt lenke på nytt.
   */
  const [receiving, setReceiving] = useState(() => hasSharedForm(window.location.search));
  const [corridors, setCorridors] = useState<CorridorOption[]>([]);
  /*
   * Stedene man kan si at man reiser fra. Følger med korridorene, så de
   * koster ingen ekstra runde til serveren.
   */
  const [origins, setOrigins] = useState<Origin[]>([]);
  /*
   * Hvilke måneder himmelen i det hele tatt kan by på noe.
   *
   * Regnet ut på serveren av den samme koden som gir poengsummen, og hentet
   * sammen med korridorene. Alternativet var et kall per månedsbytte, som ville
   * gitt en knapperad som blinker mens brukeren ser på den.
   */
  const [seasons, setSeasons] = useState<Partial<Record<Interest, number[]>>>({});
  /*
   * Månedene hvert kjøretøy kan brukes. Kommer med korridorene — se
   * `VEHICLE_SEASON` i api/src/services/winter.ts for hvorfor regelen bor der.
   */
  const [vehicleSeasons, setVehicleSeasons] = useState<Partial<Record<VehicleKind, number[]>>>({});
  /*
   * Om meldingsskjemaet er satt opp på serveren.
   *
   * Standard `false`: er svaret ikke kommet ennå, skal vi ikke vise et skjema vi
   * ikke vet virker. Det motsatte — å anta at det virker — er nettopp feilen
   * dette retter.
   */
  const [contactEnabled, setContactEnabled] = useState(false);
  const [itinerary, setItinerary] = useState<Itinerary | null>(null);
  const [allStops, setAllStops] = useState<Stop[]>([]);
  /*
   * Stoppene leses fra en ref når planen lagres.
   *
   * `generate` er en useCallback som ikke har allStops blant avhengighetene
   * sine — med god grunn, den skal ikke bygges på nytt hver gang korridoren
   * bytter. Men det gjorde at den lukket over den tomme lista fra oppstart, og
   * hver lagrede plan fikk null stopp. Offline betød det kart uten bakgrunn.
   */
  const stopsNow = useRef<Stop[]>([]);
  stopsNow.current = allStops;

  /** Samme grunn som over: `generate` lukker over planen slik den var. */
  const itineraryNow = useRef<Itinerary | null>(null);
  itineraryNow.current = itinerary;
  const [modeNotes, setModeNotes] = useState<Record<TravelMode, string> | null>(null);
  const [activeDay, setActiveDay] = useState(0);
  const [selectedStop, setSelectedStop] = useState<Stop | null>(null);
  const [hoveredStop, setHoveredStop] = useState<Stop | null>(null);
  const [loading, setLoading] = useState(false);
  /*
   * En bedt om, men ennå ikke innfridd, rulling ned til skjemaet.
   *
   * Meldt fra bruk to ganger: «velger jeg en rute — manuelt eller med AI —
   * faller siden aldri ned til innstillingene». Første forsøk flyttet
   * `scrollToForm()` inn i `handleCorridor` og trodde saken var lukket. Den var
   * ikke det, av to grunner, og begge ligger i dette flagget:
   *
   *   Fritekstsvaret går aldri gjennom `handleCorridor`. Det går gjennom
   *   `handleTextForm`, som bare fylte skjemaet og genererte. Ingen rulling ble
   *   kalt i det hele tatt.
   *
   *   Der rullingen *ble* kalt, ble den kalt for tidlig. Et klikk kaller
   *   `scrollIntoView` før React har tegnet på nytt, og `behavior: "smooth"`
   *   låser målpunktet i det øyeblikket kallet skjer. Men det samme klikket
   *   endrer alltid noe *over* skjemaet: interessepanelet klapper igjen
   *   (RouteFinder), «hvorfor planer sprekker» kommer tilbake når planen
   *   nullstilles. Skjemaet flytter seg altså etter at målet er låst, og
   *   rullingen stopper der skjemaet lå — ikke der det ble.
   *
   * Derfor ber kallestedene bare om rullingen. Selve rullingen skjer ett sted,
   * i effekten under, etter at React har lagt ut siden på nytt.
   */
  /**
   * Hvor blikket skal føres når siden er tegnet på nytt, om noe sted.
   *
   * Ett felt og ikke to flagg: to ville kunne stå sanne samtidig, og da ville
   * det siste kallet i en render avgjort hvor man landet — altså en
   * rekkefølgeavhengighet ingen kan se i koden.
   *
   *   "form"  — ruta er valgt, resten av spørsmålet stilles i skjemaet
   *   "plan"  — stedet er valgt, planen finnes alt, og det er den man kom for
   */
  const [wants, setWants] = useState<"form" | "plan" | null>(null);
  const [adjusting, setAdjusting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [mapView, setMapView] = useState<MapView>("3d");
  const [offline, setOffline] = useState(false);
  /*
   * Når planen på skjermen ble hentet, hvis den kom fra lagringen.
   *
   * Trafikkmeldinger, skredvarsel og ferjetider er ferske data, og en lagret
   * plan bærer dem fra den gang den ble laget. «Rv13 stengt» presentert som nå
   * når meldingen er tre dager gammel er ikke en unøyaktighet — det er en
   * opplysning man kan snu bilen på. Panelene får derfor vite alderen.
   */
  const [restoredAt, setRestoredAt] = useState<number | null>(null);
  /*
   * Hvilke tjenestetyper brukeren vil se på kartet og i stoppdetaljene.
   *
   * Lagret i nettleseren og ikke i URL-en: dette er en preferanse, ikke en del
   * av ruta. En delt lenke skal vise mottakerens eget filter, ikke avsenderens.
   */
  /*
   * Ruta som presenteres, når presentasjonen er åpen.
   *
   * Åpnes av et klikk i rutevelgeren og lukkes av brukeren. Valget av korridor
   * skjer med det samme klikket, så stoppene er alt på vei inn når
   * presentasjonen tegnes — den viser dem etter hvert som de kommer.
   */
  const [showcaseId, setShowcaseId] = useState<string | null>(null);
  const [placeKinds, setPlaceKinds] = useState<Set<PlaceKind>>(() => {
    try {
      const saved = localStorage.getItem("reise-planer:tjenester");
      if (saved) return new Set(JSON.parse(saved) as PlaceKind[]);
    } catch {
      // Privat vindu eller blokkert lagring. Alt på er et godt utgangspunkt.
    }
    return new Set(ALL_PLACE_KINDS);
  });

  /*
   * Verdien som står i hodet på en sammenlagt seksjon.
   *
   * Poenget er at det å legge sammen skjuler *utledningen* og aldri svaret. Er
   * feltet borte, er det fordi det ikke finnes noe svar å vise — ikke fordi
   * det ble glemt: værseksjonen har ingen score før det finnes en dato, og da
   * skal hodet være tomt framfor å vise en null.
   */
  const weatherSummary = itinerary?.outlook?.score ?? itinerary?.extendedOutlook?.score;
  /*
   * Veimeldingene, med tellingen regnet én gang.
   *
   * `?? []` fordi feltene er valgfrie på en plan hentet fra lagringen — den ble
   * lagret før feltene fantes, eller uten dem. Uten fallbacket ville en offline
   * plan krasjet på nettopp det panelet som skal si om veien er åpen.
   */
  const trafficAlerts = itinerary?.trafficAlerts ?? [];
  const trafficHazards = itinerary?.hazards ?? [];
  const trafficBlocking = blockingCount(trafficAlerts, trafficHazards);
  const costSummary = itinerary?.budget
    ? `${new Intl.NumberFormat(lang === "no" ? "nb-NO" : "en-GB").format(
        Math.round(itinerary.budget.low),
      )}–${new Intl.NumberFormat(lang === "no" ? "nb-NO" : "en-GB").format(
        Math.round(itinerary.budget.high),
      )} kr`
    : undefined;

  const changePlaceKinds = useCallback((next: Set<PlaceKind>) => {
    setPlaceKinds(next);
    try {
      localStorage.setItem("reise-planer:tjenester", JSON.stringify([...next]));
    } catch {
      // Ingenting å gjøre, og valget virker fortsatt i denne økta.
    }
  }, []);
  /** Om brukeren har slått på varsling om værskifte. Se lib/notify.ts. */
  const [notifying, setNotifying] = useState(() => notificationsAllowed());

  // Justeringer sendes umiddelbart, så raske klikk kan ellers lande i feil rekkefølge.
  const inFlight = useRef<AbortController | null>(null);
  const t = dictionaries[lang];

  /*
   * Adressen til en indekserbar side på brukerens språk.
   *
   * `contentLang` framfor `lang`: sidene finnes på norsk og engelsk, som er de
   * to språkene *innholdet* er skrevet på. En tysk bruker får engelsk side, som
   * er den samme avveiningen resten av appen alt gjør.
   */
  const langHref = (path: string) =>
    contentLang(lang) === "no" ? `${path}?lang=no` : path;

  /*
   * Ordboka for tysk, fransk og spansk hentes for seg.
   *
   * De tre er til sammen 140 kB, og en bruker trenger høyst én av dem — se
   * `dictionaries` i lib/i18n.ts for hvorfor de ikke lenger ligger i
   * hovedbunten. `loadDictionary` fyller objektet, og teller her tvinger fram
   * den ene rendringen som skal til for at teksten bytter.
   */
  const [dictReady, setDictReady] = useState(0);
  useEffect(() => {
    let live = true;
    void loadDictionary(lang).then((changed) => {
      if (changed && live) setDictReady((n) => n + 1);
    });
    return () => {
      live = false;
    };
  }, [lang]);
  // Lest for at linteren skal se at verdien brukes; den *er* utløseren.
  void dictReady;

  const corridor = corridors.find((c) => c.id === form.corridorId);

  /*
   * Kortene tones inn etter hvert som man ruller ned til dem. Kjører på nytt
   * når en plan kommer til eller språket byttes — begge deler setter nye kort
   * på siden. Se lib/reveal.ts.
   */
  useScrollReveal([itinerary, corridors.length, lang, mapView]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  /*
   * Fanetittelen følger ruta man ser på.
   *
   * To grunner, og den andre er den som gjorde det verdt å gjøre nå:
   *
   *   1. Ti åpne faner som alle heter det samme er ti faner man må klikke seg
   *      gjennom for å finne den man hadde.
   *   2. Hver rute har sin egen delbare adresse (`?rute=...`), og de står nå i
   *      sitemap.xml. Uten en engång tittel per adresse ser de ti ut som ti
   *      kopier av forsiden for en søkemotor, og «nesten like sider» er et
   *      signal man ikke vil sende.
   *
   * Merkenavnet står sist og oversettes ikke: det er et navn, ikke en setning.
   */
  useEffect(() => {
    const route = itinerary?.corridor.name ?? corridors.find((c) => c.id === form.corridorId)?.name;
    document.title = route ? `${route} · ${SITE_NAME}` : SITE_NAME;
  }, [itinerary, corridors, form.corridorId]);

  /*
   * Temaet settes på dokumentet, og systemet følges så lenge valget er «system».
   *
   * Uten lytteren ville en side som sto åpen da telefonen gikk over til
   * nattmodus blitt stående i dagmodus til den ble lastet på nytt.
   */
  useEffect(() => {
    setAppliedTheme(applyTheme(theme));
    if (theme !== "system") return;
    return watchSystem(() => setAppliedTheme(applyTheme("system")));
  }, [theme]);

  const handleTheme = useCallback((next: ThemeChoice) => {
    setTheme(next);
    rememberChoice(next);
  }, []);

  /*
   * Den sist lagde planen hentes fram ved oppstart.
   *
   * Ikke bare når `navigator.onLine` er falsk: den flagget viser om maskinen
   * har et nettverkskort, ikke om internett svarer, og den meldte «på nett»
   * midt i en avkoblet økt under testingen. Å bygge offline-støtte på den
   * ville vært å bygge på gjetning.
   *
   * I stedet vises planen alltid — er man på nett og lager en ny, erstatter
   * den denne. En reiseapp som åpner seg med turen din er dessuten riktig
   * uansett dekning.
   */
  useEffect(() => {
    // En delt lenke beskriver en annen tur enn den lagrede; da vinner lenka.
    if (hasSharedForm(window.location.search)) return;

    const saved = loadItinerary();
    if (!saved) return;

    setForm(saved.form);
    setItinerary(saved.itinerary);
    setAllStops(saved.allStops);
    setRestoredAt(saved.savedAt);
  }, []);

  // Rutene lastes én gang per språk.
  useEffect(() => {
    fetchCorridors(lang)
      .then(({
        corridors: list,
        origins: places,
        seasons: calendar,
        vehicleSeasons: vehicles,
        contactEnabled: canContact,
      }) => {
        setCorridors(list);
        setVehicleSeasons(vehicles ?? {});
        setOrigins(places ?? []);
        setSeasons(calendar ?? {});
        setContactEnabled(Boolean(canContact));
      })
      // Uten dekning er det ikke serveren som er nede — banneret sier det.
      .catch((cause) => (cause instanceof TypeError ? setOffline(true) : setError(t.apiUnreachable)));
  }, [lang, t.apiUnreachable]);

  // «Online»-hendelsen er pålitelig når den kommer: da er noe koblet til igjen.
  useEffect(() => {
    const back = () => setOffline(false);
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, []);

  /**
   * Ser etter værskifte mens appen er åpen.
   *
   * Henter planen på nytt med samme skjema og sammenligner: et utsiktspunkt som
   * har klarnet eller tåknet, eller et nordlys som plutselig står klart. Bare
   * mens fanen er synlig — en usynlig fane som poller MET hver halvtime er
   * uhøflig mot en gratis tjeneste, og brukeren ser uansett ikke svaret.
   *
   * Planen på skjermen røres ikke. Varselet sier at noe har endret seg; hva man
   * gjør med det er brukerens valg, og en rute som stokker om seg selv mens man
   * ser på den er ikke til hjelp.
   */
  useEffect(() => {
    if (!notifying || !form.startDate) return;

    const tick = async () => {
      if (document.visibilityState !== "visible") return;
      const current = itineraryNow.current;
      if (!current) return;

      try {
        const fresh = await fetchItinerary(form, lang);
        const changes = findChanges(current, fresh);
        if (changes.length > 0) await showChanges(changes, lang);
      } catch {
        // Uten dekning er det ingenting å varsle om. Banneret sier hvorfor.
      }
    };

    const timer = window.setInterval(tick, CHECK_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [notifying, form, lang]);

  // Stoppdatabasen for valgt korridor brukes til kartets bakgrunn.
  useEffect(() => {
    fetchStops(form.corridorId, lang)
      .then(({ stops, modeNotes: notes }) => {
        setAllStops(stops);
        setModeNotes(notes);
      })
      .catch((cause) => (cause instanceof TypeError ? setOffline(true) : setError(t.apiUnreachable)));
  }, [form.corridorId, lang, t.apiUnreachable]);

  const generate = useCallback(
    async (raw: TripForm, options: { keepDay?: boolean; lang?: UiLang } = {}) => {
      /*
       * Ett sted retter opp forholdet mellom dagtall og fokus. Se `usableForm`.
       * Skjemaet skrives tilbake når det ble rettet, ellers ville knapperaden
       * stått med et tall planen ikke ble laget av.
       */
      const next = usableForm(raw);
      if (next !== raw) setForm(next);
      // En justering er en ny generering, men skal ikke blanke ut ruten under seg.
      const setBusy = options.keepDay ? setAdjusting : setLoading;
      const useLang = options.lang ?? lang;

      inFlight.current?.abort();
      const controller = new AbortController();
      inFlight.current = controller;

      setBusy(true);
      setError(null);
      try {
        const result = await fetchItinerary(next, useLang, controller.signal);
        setItinerary(result);
        // Ferskt svar: varslene gjelder nå, og alderen skal ikke vises lenger.
        setRestoredAt(null);
        /*
         * `keepDay` er en justering: brukeren fjernet et stopp og skal bli
         * stående på dagen de så på. Men planen kan ha blitt kortere av
         * nettopp det — tar man bort nok stopp, følger dagtallet stoppene, se
         * selectStops. Sto man på dag 6 av en plan som nå har tre, pekte
         * `activeDay` utenfor lista, og tavla returnerte null: hele
         * reiseplanen forsvant fra skjermen etter et helt vanlig klikk på
         * fjern-knappen.
         */
        setActiveDay((current) =>
          options.keepDay ? Math.min(current, result.days.length - 1) : 0,
        );
        syncUrl(next, useLang);
        // Planen kommer fra en POST, som service workeren ikke kan bufre.
        // Uten dette ville appen åpnet seg tom på Finse.
        saveItinerary(next, result, stopsNow.current, useLang);
        // Bildene bufres nå, mens det er dekning — ikke når man er i fjellet.
        prefetchImages(result.days.flatMap((day) => day.stops));
      } catch (cause) {
        // En avbrutt forespørsel er erstattet av en nyere — ikke en feil å vise.
        if (cause instanceof DOMException && cause.name === "AbortError") return;

        /*
         * `fetch` kaster TypeError når nettet er borte, og en vanlig Error når
         * serveren svarte med en feil. Skillet avgjør hva brukeren får se: uten
         * dekning er den lagrede planen svaret, ikke en feilmelding.
         */
        if (cause instanceof TypeError) {
          setOffline(true);
          const saved = loadItinerary();
          if (saved && !itineraryNow.current) {
            setForm(saved.form);
            setItinerary(saved.itinerary);
            setAllStops(saved.allStops);
            setRestoredAt(saved.savedAt);
          }
          return;
        }

        setError(cause instanceof Error ? cause.message : t.genericError);
      } finally {
        if (inFlight.current === controller) {
          inFlight.current = null;
          setBusy(false);
        }
      }
    },
    [lang, t.genericError],
  );

  /** Justering: oppdater skjemaet og hent ruten på nytt med én gang. */
  const adjust = useCallback(
    (patch: Partial<TripForm>) => {
      setForm((current) => {
        const next = { ...current, ...patch };
        void generate(next, { keepDay: true });
        return next;
      });
    },
    [generate],
  );

  const handleAddStop = useCallback(
    (stopId: string) => {
      adjust({
        include: [...new Set([...form.include, stopId])],
        exclude: form.exclude.filter((id) => id !== stopId),
      });
    },
    [adjust, form.include, form.exclude],
  );

  const handleRemoveStop = useCallback(
    (stopId: string) => {
      adjust({
        exclude: [...new Set([...form.exclude, stopId])],
        include: form.include.filter((id) => id !== stopId),
      });
    },
    [adjust, form.include, form.exclude],
  );

  const handleResetAdjustments = useCallback(() => {
    adjust({ include: [], exclude: [] });
  }, [adjust]);

  /**
   * «Bare dette stedet», og veien tilbake til hele ruta.
   *
   * Meldt fra bruk: «velger jeg bare ett stopp, hvorfor kan jeg ikke få en
   * rute bare for det? Jeg må ende opp med å planlegge hele ruta og fjerne de
   * andre stoppene, og det er veldig irriterende».
   *
   * `include` står bevisst urørt. Det er det som gjør veien tilbake hel: slår
   * man av igjen, kommer man tilbake til nøyaktig planen man kom fra, med
   * stedet fortsatt festet. Ryddet vi det, ville «hele ruta igjen» vært en
   * knapp som også kastet valget brukeren tok rett før.
   */
  const handleFocusStop = useCallback(
    (stopId: string | null) => {
      adjust({ focusStopId: stopId ?? "" });
    },
    [adjust],
  );

  /**
   * Fra forhåndsvisningen i «ett sted»-panelet til en ekte plan.
   *
   * Panelet kunne vise dagene, men det fantes ingen vei videre derfra: for å få
   * etapper, vær, budsjett, klesråd og roadbook måtte man over i rutevelgeren
   * og bygge hele korridoren på nytt. Det er den samme irritasjonen som over,
   * bare et annet sted i appen.
   *
   * `include`/`exclude` nullstilles her, i motsetning til i `handleFocusStop`:
   * de gjelder stoppene i en *annen* rute enn den vi nå går til, og et festet
   * stopp fra Bergensbanen har ingenting i en plan om Lofoten å gjøre.
   */
  const handlePlanPlace = useCallback(
    (stopId: string, days: number, corridorId: string, fromStopId?: string) => {
      setForm((current) => {
        const next = {
          ...current,
          corridorId,
          days,
          focusStopId: stopId,
          /*
           * Bare når kalleren faktisk spurte. «Ett sted»-panelet har en egen
           * velger, og tom streng der betyr «jeg sier ikke noe». «Utforsk» har
           * ingen velger i det hele tatt — den skal da la det stå som det er,
           * framfor å blanke ut et valg brukeren tok i skjemaet.
           */
          fromStopId: fromStopId ?? current.fromStopId,
          include: [],
          exclude: [],
        };
        void generate(next);
        return next;
      });
      /*
       * Rett på planen, ikke på skjemaet. Meldt fra bruk: «velger jeg
       * Trolltunga, hvorfor lander jeg her?» — med et bilde av skjemaet.
       * Ønsket står til planen finnes; se effekten som lytter på `wants`.
       */
      setWants("plan");
    },
    [generate],
  );

  /** Bytte av språk henter ruten på nytt, så serverteksten følger med. */
  const handleLanguage = useCallback(
    (next: UiLang) => {
      setLang(next);
      rememberLang(next);
      if (itinerary) void generate(form, { keepDay: true, lang: next });
      else syncUrl(form, next);
    },
    [form, generate, itinerary],
  );

  /** Bytte av rute nullstiller manuelle justeringer — stoppene finnes ikke der. */
  /**
   * Skjemaet slik språkmodellen fylte det ut.
   *
   * Går rett videre til `generate`, som er den samme veien knappene i skjemaet
   * bruker. Fritekst gir altså ingen egen plan — den gir de samme feltene, og
   * hele planleggeren under er uendret.
   */
  const handleTextForm = useCallback(
    (next: TripForm) => {
      setForm(next);
      void generate(next);
      /*
       * Fritekstveien er den som aldri rullet. Den setter ruta gjennom et helt
       * skjema fra språkmodellen og går utenom `handleCorridor` — så den må be
       * om rullingen selv. Se `wants`.
       */
      setWants("form");
    },
    [generate],
  );

  /**
   * Ruta er valgt — uansett hvordan.
   *
   * Meldt fra bruk: «når jeg velger en rute faller den aldri ned til
   * valgområdet — sjekk for hver forvalgt rute, fra AI, og manuelt».
   *
   * `scroll` er ikke pynt på signaturen. Å *velge* en rute og å *kikke* på den
   * er to forskjellige handlinger, og de kommer begge hit: kortet velger,
   * øyeknappen åpner presentasjonen. Bare den første skal føre blikket videre.
   * Presentasjonen dekker hele skjermen, så en rulling under den er en side som
   * har flyttet seg mens man så en annen vei.
   *
   * Merk at valget her *ikke* ruller selv. Det ber om rulling, og effekten som
   * lytter på `wants` gjør jobben etter at siden er tegnet på nytt.
   */
  const handleCorridor = useCallback(
    (corridorId: string, { scroll = true }: { scroll?: boolean } = {}) => {
      const next = corridors.find((c) => c.id === corridorId);

      setForm((current) => ({
        ...current,
        corridorId,
        /*
         * Dekker ikke den nye ruta valget, faller vi til *kollektivt* — aldri
         * ut av familien.
         *
         * Her sto `next.modes[0]`, og `corridor.modes` er skrevet bil-først
         * for hver rute uten jernbane: et togvalg ble derfor bil. Det er den
         * samme feilen serveren hadde, og reserven er nå den samme som der.
         * `modes[0]` er nå tilfeldigvis «kollektiv» siden den står først i
         * dataene, men å hvile på rekkefølgen i en array er å hvile på noe
         * ingen test holder fast.
         */
        mode:
          next && !next.modes.includes(current.mode)
            ? current.mode === "bil"
              ? "bil"
              : "kollektiv"
            : current.mode,
        /*
         * Lengden følger med til den nye ruta.
         *
         * Før ble den snappet til nærmeste forslag, fordi et valg uten knapp
         * så ut som at skjemaet hadde glemt hva brukeren trykket. Knapperaden
         * viser nå alltid det valgte tallet, så symptomet er borte — og da er
         * snappingen bare en tur brukeren ikke ba om. Står åtte dager, blir det
         * åtte dager, med en merknad om hva det betyr for akkurat denne ruta.
         */
        days: current.days,
        /*
         * Å velge en rute er å si at turen *ikke* handler om ett sted lenger.
         *
         * Sto fokuset igjen her, gjorde ikke «se hele ruta» det den lovte —
         * man fikk den samme ett-sted-planen med en ny korridor under seg. Og
         * dagtallet retter seg selv: se `usableForm`.
         */
        focusStopId: "",
        include: [],
        exclude: [],
      }));
      setItinerary(null);
      setSelectedStop(null);

      // Se doc-kommentaren: her *bes* det om rulling. Den skjer etter tegningen.
      if (scroll) setWants("form");
    },
    [corridors],
  );

  /**
   * Rullingen ned til skjemaet, ett sted, etter at siden er tegnet på nytt.
   *
   * `requestAnimationFrame` framfor å rulle rett i effekten: React har lagt
   * inn de nye nodene når effekten kjører, men vi vil ha målet lest av *etter*
   * at nettleseren har regnet ut hva siden nå ser ut som. Én ramme holder — det
   * som skjer i samme klikk (panelet som klapper igjen, seksjonen som kommer
   * tilbake) er ferdig lagt ut da. Flere rammer ville ikke hjulpet på det som
   * kommer senere, og det er nettopp derfor ønsket ikke slettes ved første
   * forsøk:
   *
   *   Er en plan under arbeid, flytter skjemaet seg en gang til når svaret
   *   kommer — «hvorfor planer sprekker» forsvinner over det. Ingen mengde
   *   rammer venter ut et nettverkskall. Ønsket står derfor til `loading` er
   *   over, og effekten kjører på nytt og retter opp landingen da.
   *
   *   Er skjemaet ikke montert i det hele tatt — en delt lenke viser bare
   *   turen til mottakeren har trykket «endre» — sier `scrollToForm()` fra at
   *   ingenting ble rullet, og ønsket blir stående til skjemaet finnes.
   *
   * Presentasjonen er unntaket: står den åpen, venter vi. Å rulle bak et
   * fullskjermsvindu er å flytte siden mens ingen ser det.
   */
  useEffect(() => {
    if (!wants || showcaseId) return;
    const frame = requestAnimationFrame(() => {
      /*
       * `scrollToPlan()` svarer `false` til tavla finnes. Ønsket blir derfor
       * stående gjennom hele hentingen og lander når planen er der — samme
       * mekanisme som holdt skjemaet i sikte mens svaret kom.
       */
      const landed = wants === "plan" ? scrollToPlan() : scrollToForm();
      if (landed && !loading) setWants(null);
    });
    return () => cancelAnimationFrame(frame);
    /*
     * `itinerary` og `receiving` brukes ikke i kroppen, og står her likevel:
     * de er de to tingene som endrer siden *over* skjemaet etter at ønsket ble
     * meldt. Planen som kommer fjerner «hvorfor planer sprekker»; «ta over»
     * setter hele planleggeren på skjermen for den som fikk lenken tilsendt.
     * Uten dem ville effekten sovet gjennom nettopp de to bevegelsene den
     * finnes for å svare på.
     */
  }, [wants, showcaseId, loading, itinerary, receiving]);

  // En delt lenke skal vise ruten med én gang, uten at mottakeren må trykke.
  useEffect(() => {
    if (hasSharedForm(window.location.search)) {
      void generate(formFromUrl(window.location.search));
    }
    // Kjøres bevisst bare ved oppstart.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (patch: Partial<TripForm>) => setForm((current) => ({ ...current, ...patch }));

  const handleCopyLink = async () => {
    const url = `${window.location.origin}${window.location.pathname}${formToQuery(form, lang)}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      setError(t.clipboardBlocked);
    }
  };

  const routeStops = useMemo(() => itinerary?.days.flatMap((day) => day.stops) ?? [], [itinerary]);

  /**
   * Stoppene kartet tegner en strek gjennom.
   *
   * ── Hvorfor det ikke er de samme som `routeStops` ────────────────────────
   *
   * Meldt fra bruk: «når ett stopp er valgt, vises ikke fly-ruta — kan vi vise
   * fra og til så den kan fly?»
   *
   * Og det stemte. En plan om ett sted har nøyaktig ett stopp; ett punkt er
   * ingen strek, og «fly ruta» sto deaktivert på nettopp den planen som har
   * mest å fly til. Men reisen har to ender til: veien *fram* dit, og veien
   * hjem. Begge står alt i planen som egne etapper — de manglet bare på kartet.
   *
   * ── Hvorfor de tas med alltid, ikke bare når det trengs ──────────────────
   *
   * Fordi «bare når planen er kort» ville gjort kartet til noe som skifter
   * mening: den samme reisen tegnet på to måter, avhengig av hvor mange dager
   * man ba om. Reisen fra Oslo til Stavanger er like sann på en femdagerstur
   * som på en endagers, og appen viser den alt som en etappe med avganger.
   *
   * Ingen ender uten en adkomst eller en hjemreise, så en helt vanlig
   * ruteplan ser ut nøyaktig som før. Startbyen faller dessuten bort av seg
   * selv når den *er* første stopp — se `approach` i planleggeren.
   */
  const mapStops = useMemo(() => {
    const ends = itinerary?.endpoints ?? [];
    if (ends.length === 0) return routeStops;

    const from = itinerary?.approach ? ends.find((s) => s.id === itinerary.approach!.fromStopId) : undefined;
    const home = itinerary?.returnLeg ? ends.find((s) => s.id === itinerary.returnLeg!.toStopId) : undefined;

    const chain = [...(from ? [from] : []), ...routeStops, ...(home ? [home] : [])];
    /*
     * Et sted kan stå i begge ender — «hjem til Oslo» på en tur som startet i
     * Oslo. To pinner på samme punkt er én pinne for øyet og to for koden.
     * Nabo-duplikater er det eneste som kan oppstå her.
     */
    return chain.filter((stop, index) => index === 0 || stop.id !== chain[index - 1].id);
  }, [itinerary, routeStops]);
  /*
   * Hvilke av de fem etappeikonene som faktisk brukes på denne ruta.
   *
   * Reisemåten er den samme for hele turen, så en fullstendig liste over alle
   * fem ville vist «tog» og «buss» og «bil» samtidig i forklaringen under et
   * kart som bare bruker ett av dem. `fot` er unntaket — den dukker opp for de
   * stoppene som uansett bare nås gående, og bare der. Se vehicleGlyphs.ts.
   */
  const usedVehicleGlyphs = useMemo(() => {
    if (!itinerary || routeStops.length < 2) return [];
    const keys = new Set<VehicleGlyphKey>();
    for (let i = 0; i < routeStops.length - 1; i++) {
      keys.add(vehicleGlyphKeyFor(routeStops[i + 1], itinerary.request.mode, itinerary.request.vehicle));
    }
    return VEHICLE_GLYPHS.filter((glyph) => keys.has(glyph.key));
  }, [itinerary, routeStops]);
  const activeIds = useMemo(() => {
    const ids = itinerary?.days[activeDay]?.stops.map((s) => s.id) ?? [];
    return selectedStop ? [...ids, selectedStop.id] : ids;
  }, [itinerary, activeDay, selectedStop]);

  return (
    <>
      {/*
        Landskapet ligger utenfor `.shell`, ikke inni. `.shell` har sin egen
        stablingskontekst for å komme over scenen, og et element inni den kan
        ikke legge seg bak den — se z-index på begge i styles.css.
      */}
      <FjordBackdrop />
      <div className="shell">
        <div className="container">
        <header className="masthead">
          <div className="masthead-top">
            {/*
              Bare navnet på appen. Rutenavnet sto her også, som
              «Reiseplanlegger · Bodø—Lofoten—Tromsø», og det var én gjentakelse
              for mye: ruta står alt øverst i selve planen, og på telefon deler
              denne linja plass med temabryteren og fem språkknapper på 360 piksler.
            */}
            <div className="eyebrow">{t.eyebrow}</div>
            <ThemeToggle choice={theme} onChange={handleTheme} lang={lang} />
            <div className="lang-switch no-print" role="group" aria-label={t.languageLabel}>
              {LANGUAGES.map((option) => (
                <button
                  key={option.id}
                  className={`lang ${option.id === lang ? "lang--on" : ""}`}
                  aria-pressed={option.id === lang}
                  /*
                    Navnet står også som etikett, ikke bare som synlig tekst.
                    På telefon skjules teksten — fem knapper med navn får ikke
                    plass på 360 piksler — og uten dette ville knappen da vært
                    et flagg uten navn for en skjermleser.
                  */
                  aria-label={option.label}
                  onClick={() => handleLanguage(option.id)}
                >
                  <FlagIcon lang={option.id} />
                  <span className="lang-label">{option.label}</span>
                </button>
              ))}
            </div>
          </div>
          {/*
            Sagt rett ut når grensesnittet er oversatt og innholdet ikke er.
            Se `UiLang` i types.ts: stedsbeskrivelsene er redaksjonell prosa på
            norsk og engelsk, og å late som noe annet ville vært å love en
            oversettelse som ikke finnes.
          */}
          {!hasTranslatedContent(lang) && (
            <p className="content-lang-note no-print">
              <Languages size={12} /> {t.contentInEnglish}
            </p>
          )}
          <div className="masthead-headline">
            <h1>{t.title}</h1>
            <p className="lede">{t.lede}</p>
            {/*
              Hintet om at siden fortsetter. Toppen fyller nå nesten hele
              skjermen, og en skjermfyllende topp ser ut som hele siden.
            */}
            <div className="masthead-scroll no-print" aria-hidden="true">
              {t.scrollCue}
            </div>
          </div>
        </header>

        {/* Uten dekning skal det stå hvorfor tallene ikke oppdaterer seg. */}
        {offline && (
          <div className="banner banner--offline no-print">
            <WifiOff size={16} />
            <span>
              <strong>{t.offlineTitle}</strong> {t.offlineBody}
            </span>
          </div>
        )}

        {/*
          Varsling om værskifte.
          
          Knappen står øverst og ikke i kontrollpanelet, fordi den ikke er en
          del av å lage en plan — den gjelder turen etterpå. Hintet sier rett ut
          at varslene kommer fra denne enheten: et varsel man tror vekker en
          lukket telefon, men som ikke gjør det, er verre enn ingen varsling.
        */}
        {canNotify() && itinerary && form.startDate && (
          <div className="notify-row no-print">
            <button
              type="button"
              className={`ghost-button ${notifying ? "ghost-button--on" : ""}`}
              onClick={async () => {
                if (notifying) {
                  setNotifying(false);
                  return;
                }
                setNotifying(await requestPermission());
              }}
            >
              {notifying ? <BellRing size={13} /> : <Bell size={13} />}
              {notifying ? t.notifyOn : t.notifyOff}
            </button>
            <span className="notify-hint">
              {Notification.permission === "denied" ? t.notifyBlocked : t.notifyHint}
            </span>
          </div>
        )}

        {error && (
          <div className="banner banner--error no-print">
            <TriangleAlert size={16} />
            <span>{error}</span>
          </div>
        )}

        {/*
          Hvorfor appen finnes, før man blir bedt om å bruke den.

          Vises bare før en rute er laget. Har man en plan på skjermen, er
          spørsmålet besvart — da er kortene i veien for det man kom for.
        */}
        {!receiving && !itinerary && <WhyItinerariesBreak lang={lang} />}

        {/*
          Én knapp i stedet for hele planleggeren, når turen kom fra noen andre.
          
          Den står *over* planen fordi det er der man ser etter den: «dette er
          Karis tur — kan jeg endre den?» er spørsmålet man har før man har
          lest dagene, ikke etter.
        */}
        {receiving && (
          <button type="button" className="take-over no-print" onClick={() => setReceiving(false)}>
            <Sparkles size={14} />
            {t.sharedEdit}
          </button>
        )}

        {/*
          De tre inngangene: utforsk, ett sted, og når du bør komme.

          ── Hvorfor de alltid står ────────────────────────────────────────

          De sto først bak `!itinerary`, og siden bak «ingen plan, eller en
          hentet fram fra lagringen». Begge reglene var forsvarlige, og begge
          var feil på samme måte: de gjorde *tilstedeværelsen* til noe brukeren
          måtte gjette seg til. Meldt fra bruk to ganger — «kommer bare av og
          til, og aldri første gang», og deretter «de kommer tilfeldig når jeg
          trykker på noe; kan de ikke bare være der?».

          Svaret er ja, og prisen er null: panelene starter sammenlagt, så hver
          av dem er én linje med en overskrift og et sammendrag. Det var
          nettopp *derfor* de ble lagt sammen. Et panel som alltid står, og som
          er lukket til man vil ha det, kan man finne igjen; et som kommer og
          går etter en regel om hva slags plan som ligger på skjermen, kan man
          ikke.

          Unntaket er `receiving`: den som har fått en delt lenke ser turen noen
          andre lagde, og skal ikke møte tre innganger til sin egen først.

          Rekkefølgen er hvor mye de forutsetter. «Utforsk» forutsetter
          ingenting, «bare til ett sted» at du vet stedet, «når bør du komme» at
          du vet hva du kommer for. Rutevelgeren under forutsetter mest av alle.
        */}
        {!receiving && (
          <ExplorePanel
            lang={lang}
            corridors={corridors}
            onOpenRoute={(corridorId) => handleCorridor(corridorId)}
            onPlanTrip={handlePlanPlace}
          />
        )}

        {!receiving && (
          <PlaceTripPanel
            onPlanTrip={handlePlanPlace}
            lang={lang}
            origins={origins}
            onOpenRoute={(corridorId) => handleCorridor(corridorId)}
          />
        )}

        {!receiving && <MonthAnswer lang={lang} corridorId={form.corridorId} />}

        {!receiving && (
        <RouteFinder
          seasons={seasons}
          startDate={form.startDate}
          lang={lang}
          days={form.days}
          onPick={handleCorridor}
          onDescribed={handleTextForm}
        />
        )}

        {!receiving && (
        <CorridorPicker
          corridors={corridors}
          selected={form.corridorId}
          lang={lang}
          onSelect={(id) => {
            /*
             * Meldt fra bruk: «velger jeg en rute, faller den aldri ned til de
             * valgte innstillingene». Klikket åpnet presentasjonen, og lukket
             * man den, sto siden helt som før — valget hadde skjedd, men
             * ingenting på skjermen sa det, og skjemaet lå to skjermhøyder ned.
             *
             * Nå velger klikket ruta og fører blikket dit valget får følger.
             * Presentasjonen har fått sin egen knapp på kortet.
             */
            handleCorridor(id);
          }}
          onPreview={(id) => {
            /*
             * Valget først: presentasjonen henter stoppene sine gjennom
             * effekten som følger `form.corridorId`, og uten valget har den
             * ingenting å vise.
             *
             * Men uten rulling. Dette er «vis meg denne ruta», ikke «kom
             * videre» — og rullingen ville uansett skjedd bak presentasjonen,
             * som dekker hele skjermen. Man ba om å se, og hadde fått en side
             * som sto et annet sted da man lukket den igjen. «Planlegg denne
             * ruta» inne i presentasjonen er den som ber om rullingen.
             */
            handleCorridor(id, { scroll: false });
            setShowcaseId(id);
          }}
        />
        )}

        {/*
          Når det er fullt av folk på ruta.

          Rett under rutevalget, og *ikke* inne i «når bør du komme?». Det er
          der tallene sto før, og der forsvant de: som to setninger med terskel
          på 35 og 80, i et panel som skjules i det en plan finnes. Meldt fra
          bruk som «hvor kan jeg se den?» — og et tall ingen finner er et tall
          appen ikke har.

          Her står det til ruta er byttet, plan eller ei, fordi det er et
          faktum om ruta og ikke om planen.
        */}
        {!receiving && corridor?.crowd && (
          <CrowdCalendar lang={lang} crowd={corridor.crowd} />
        )}

        {!receiving && (
        <ControlPanel
          vehicleSeasons={vehicleSeasons}
          form={form}
          onChange={handleChange}
          onSubmit={() => void generate(form)}
          loading={loading}
          modeNotes={modeNotes}
          lang={lang}
          corridor={corridor}
          origins={origins}
          onChangeRoute={() => scrollToAnchor("route-picker")}
        />
        )}

        {itinerary?.warnings.map((warning) => (
          <div className="banner banner--warn no-print" key={warning}>
            <AlertTriangle size={16} />
            <span>{warning}</span>
          </div>
        ))}

        {itinerary && (
          <>
            {/*
              «Sushants tur», over tallene.

              Vises bare når et navn finnes, og navnet i skjemaet er det som
              gjelder — enten brukeren skrev det selv eller det kom inn med
              lenka. Overskriften står her og ikke i delepanelet langt nede,
              fordi det er her man ser etter svaret på «hva er dette»: en delt
              lenke åpner seg med en ferdig plan, og uten et navn over den ser
              den ut som appens egen. Eieformen kommer fra ordboka, ikke fra en
              apostrof i koden — se `tripOwnedBy` i lib/i18n.ts.
            */}
            {form.ownerName && <h2 className="trip-owner">{t.tripOwnedBy(form.ownerName)}</h2>}

            {/*
              ── Turen i fire tall, og bare de som betyr noe ─────────────────

              Meldt fra bruk: «disse to kan slås sammen og være bare én kort
              øverst — mange klager på at det er for mye data».

              De to stripene sa nesten det samme. Denne sto med avstand,
              reisetid, høyeste punkt og antall stopp; «turen i tall» lenger
              nede gjentok avstand, stopp og høyeste punkt, og la til dager,
              klartimer, fotturtimer, drivstoff, strøm og bompenger. Høyeste
              punkt er tatt bort her — det står i kortet, og det er en detalj,
              ikke et svar på «hva er dette». **Dager** er kommet inn, som er
              det første man lurer på og som ingen av stripene hadde.

              ── Og nullene er borte ────────────────────────────────────────

              En plan om ett sted har ingen etapper mellom stopp: «0 km» og
              «0 min» er sant og fullstendig ubrukelig, og det var det
              brukerens eget skjermbilde viste — 0 KM, 0 min, 0 liter, 0 kWh.
              Et tall som alltid er null for en hel plantype er ikke data, det
              er støy. Feltet uteblir i stedet.
            */}
            <div className="stats">
              <div className="stat">
                <div className="stat-value">{itinerary.days.length}</div>
                <div className="stat-label">{t.statDays}</div>
              </div>
              <div className="stat">
                <div className="stat-value">{itinerary.totalStops}</div>
                <div className="stat-label">{t.statStops}</div>
              </div>
              {itinerary.totalDistanceKm > 0 && (
                <div className="stat">
                  <div className="stat-value">
                    {itinerary.totalDistanceKm}
                    <small>{t.unitKm}</small>
                  </div>
                  <div className="stat-label">{t.statDistance}</div>
                </div>
              )}
              {itinerary.totalTravelHours > 0 && (
                <div className="stat">
                  <div className="stat-value">{formatHours(itinerary.totalTravelHours, lang)}</div>
                  <div className="stat-label">{t.statTravel}</div>
                </div>
              )}
            </div>

            {/*
              Planen i to spalter.

              Rekkefølgen var problemet, ikke mengden. Panelene kom i den
              rekkefølgen de ble bygget, så ruta — det man kom for — sto som
              nummer tolv, etter vær, beste dag, optimalisering, budsjett,
              klesråd, deling, lyd, kart, høydeprofil, trafikk og ferjer. Man
              måtte dra seg forbi hele appen for å se hvilke steder man skulle
              til.

              Nå står ruta først i hovedspalten, og det som beskriver den —
              kart, trafikk, ferjer — under den. Alt som er *oppslagsverk*
              flyttes til sidespalten: vær, kostnad, terreng, deling. På smale
              skjermer finnes ingen sidespalte, så den legger seg under og
              seksjonene starter sammenlagt. Se lib/fold.ts.

              DOM-rekkefølgen er den nye rekkefølgen, ikke bare den viste.
              Å flytte spaltene med CSS `order` ville gitt riktig bilde og feil
              lese- og tabulatorrekkefølge — WCAG 1.3.2 og 2.4.3.
            */}
            <div className="plan">
              <div className="plan-main">
                            {/*
                              Værvinduet står FØR ruta, ikke i sidespalten.
                            
                              Meldt fra bruk: på mobil kom «beste værvindu» helt til slutt, og det er
                              feil rekkefølge på spørsmålene — *når* man skal reise avgjøres før man
                              leser *hvor* man skal. På en telefon finnes ingen sidespalte, så alt
                              som lå der havnet under hele ruta.
                            
                              Flyttet i DOM-en framfor med CSS `order`: en rekkefølge som bare
                              stemmer visuelt gir feil lese- og tabulatorrekkefølge, og den måtte
                              dessuten vedlikeholdes per bruddpunkt. Seksjonen er sammenlagt som
                              før, så den koster én rad.
                            */}
                            <FoldPanel
                              id="weather"
                              title={t.foldWeather}
                              icon={<CloudSun size={14} />}
                              summary={weatherSummary}
                            >
                              {/*
                                Klimaanslaget står over de to værpanelene, og det er med vilje.

                                Ligger datoen utenfor varselet, er «beste avreisedag» og de
                                værstyrte dagsgrensene begge av — og da må det stå *hvorfor*
                                før man leser at de er av, ellers ser appen ut som at den ikke
                                klarte å regne. Panelet setter seg selv til side når det finnes
                                et ekte varsel: feltet er da ikke satt i det hele tatt.
                                Se api/src/services/outlook.ts.
                              */}
                              {itinerary.outlook && <OutlookPanel outlook={itinerary.outlook} lang={lang} />}

                              {/*
                                Langtidsvarselet, i sjiktet mellom de to horisontene.

                                Gjensidig utelukkende med panelet over — serveren setter aldri
                                begge feltene — og det er en regel og ikke en tilfeldighet: det
                                finnes ett svar på været, og to paneler som begge svarte ville
                                invitert til å tro på det snilleste. Se api/src/services/extended.ts.
                              */}
                              {itinerary.extendedOutlook && (
                                <ExtendedPanel outlook={itinerary.extendedOutlook} lang={lang} />
                              )}

                              {itinerary.bestDeparture && (
                                <BestDayPanel
                                  bestDeparture={itinerary.bestDeparture}
                                  currentDate={form.startDate}
                                  lang={lang}
                                  onPickDate={(date) => adjust({ startDate: date })}
                                />
                              )}

                              {itinerary.optimization && (
                                <OptimizationPanel optimization={itinerary.optimization} lang={lang} />
                              )}
                            </FoldPanel>

                            {/*
                              Etappen fram til der ruta begynner.

                              Meldt fra bruk: «hva om jeg har lyst til å starte
                              rute fra Stavanger, men jeg bor i Oslo». Over
                              tidslinja og ikke inni den, fordi den ikke er en
                              dag på turen — den er reisen fram til dag 1, og
                              å nummerere den som en dag ville flyttet hele
                              planen ett hakk.

                              Samme rad som etappene inne i planen, så den
                              leses likt: avgangene der de finnes, avstand og
                              anslag der de ikke gjør, og «det går også tog her»
                              når man har tenkt å kjøre.
                            */}
                            {itinerary.approach && (
                              <section className="card approach">
                                <h3 className="card-title">
                                  {t.approachHeading(itinerary.approach.toName)}
                                </h3>
                                <ConnectionRow
                                  connection={itinerary.approach}
                                  from={origins.find((o) => o.id === form.fromStopId)}
                                  to={itinerary.days[0]?.stops[0]}
                                  lang={lang}
                                />
                              </section>
                            )}

                            {/* Planen har sin egen grense — se ErrorBoundary.tsx. */}
                            <ErrorBoundary label={t.partPlan} lang={lang} resetKeys={[itinerary]}>
                            <ItineraryBoard
                              itinerary={itinerary}
                              activeDay={activeDay}
                              onDayChange={setActiveDay}
                              onSelectStop={setSelectedStop}
                              onExportCalendar={() => openInCalendar(form, lang)}
                              onCopyLink={() => void handleCopyLink()}
                              copied={copied}
                              onAddStop={handleAddStop}
                              onRemoveStop={handleRemoveStop}
                              onResetAdjustments={handleResetAdjustments}
                              onFocusStop={handleFocusStop}
                              adjusting={adjusting}
                              lang={lang}
                              onHoverStop={setHoveredStop}
                            />
                            </ErrorBoundary>

                            <div className="card map-card">
                              <div className="card-title">
                                <MapIcon size={14} style={{ verticalAlign: -2, marginRight: 7 }} />
                                {t.mapTitle}
                                <div className="map-views no-print" role="group" aria-label={t.mapTitle}>
                                  <button
                                    type="button"
                                    className={`map-view ${mapView === "3d" ? "map-view--on" : ""}`}
                                    aria-pressed={mapView === "3d"}
                                    onClick={() => setMapView("3d")}
                                  >
                                    {t.mapView3d}
                                  </button>
                                  <button
                                    type="button"
                                    className={`map-view ${mapView === "flat" ? "map-view--on" : ""}`}
                                    aria-pressed={mapView === "flat"}
                                    onClick={() => setMapView("flat")}
                                  >
                                    {t.mapViewFlat}
                                  </button>
                                </div>
                              </div>
                              <p className="card-sub">{mapView === "3d" ? t.map3dSubtitle : t.mapSubtitle}</p>

                              {mapView === "3d" ? (
                                <>
                                  {/*
                                    Grensa står *utenfor* Suspense, ikke inni.
                                    Et avvist `lazy()` kastes under render, og
                                    Suspense fanger bare det som venter — se
                                    lib/chunk.ts. Uten dette tok kartet med seg
                                    hele planen når det falt.
                                  */}
                                  <ErrorBoundary
                                    label={t.partMap}
                                    lang={lang}
                                    resetKeys={[mapView, itinerary]}
                                  >
                                  <Suspense
                                    fallback={<div className="terrain3d-loading">{t.mapLoading}</div>}
                                  >
                                    <TerrainMap3D
                                      allStops={allStops}
                                      routeStops={mapStops}
                                      activeIds={activeIds}
                                      onSelect={setSelectedStop}
                                      onHover={setHoveredStop}
                                      lang={lang}
                                      placeKinds={placeKinds}
                                      tripMode={itinerary?.request.mode}
                                      tripVehicle={itinerary?.request.vehicle}
                                      theme={appliedTheme}
                                      onUnavailable={() => setMapView("flat")}
                                    />
                                  </Suspense>
                                  </ErrorBoundary>
                                  {/* WebGL-lerretet kommer ikke med på papir, så det skjematiske
                                      kartet tegnes usynlig ved siden av og trer fram ved utskrift. */}
                                  <div className="print-only">
                                    <RouteMap
                                      allStops={allStops}
                                      routeStops={mapStops}
                                      activeIds={activeIds}
                                      onSelect={setSelectedStop}
                                    />
                                  </div>
                                </>
                              ) : (
                                <RouteMap
                                  allStops={allStops}
                                  routeStops={mapStops}
                                  activeIds={activeIds}
                                  onSelect={setSelectedStop}
                                  onHover={setHoveredStop}
                                />
                              )}

                              <div className="map-legend">
                                {Object.entries(TAG_COLOR).map(([tag, color]) => (
                                  <span className="legend-item" key={tag}>
                                    <span className="legend-dot" style={{ background: color }} />
                                    {t.tagName[tag as keyof typeof t.tagName]}
                                  </span>
                                ))}
                                <span className="legend-item">
                                  <span className="legend-dot" style={{ background: "#8A9A9F", opacity: 0.5 }} />
                                  {t.legendNotChosen}
                                </span>
                              </div>

                              {/*
                                Filteret står mellom kartet og forklaringen: man ser punktene,
                                velger hva man vil se, og leser hva merkene betyr.
                              */}
                              <PlaceFilter
                                stops={routeStops}
                                active={placeKinds}
                                onChange={changePlaceKinds}
                                lang={lang}
                              />

                              {/* Hva merkene under hvert stopp betyr. Se SERVICE_MARKS i RouteMap. */}
                              <div className="map-legend map-legend--services">
                                <span className="legend-label">{t.mapLegendTitle}</span>
                                {SERVICE_MARKS.map(({ key, icon: Icon, color }) => (
                                  <span className="legend-item" key={key}>
                                    <span className="legend-mark" style={{ borderColor: color, color }}>
                                      <Icon size={9} strokeWidth={2.4} />
                                    </span>
                                    {t.mapLegend[key]}
                                  </span>
                                ))}
                              </div>

                              {/*
                                Hva ikonene langs streken betyr — se lib/vehicleGlyphs.ts. Bare
                                de som faktisk forekommer på denne ruta: reisemåten er én for
                                hele turen, og en full liste ville lovet et togikon på en
                                busstur.
                              */}
                              {usedVehicleGlyphs.length > 0 && (
                                <div className="map-legend map-legend--services">
                                  <span className="legend-label">{t.mapLegendVehicleTitle}</span>
                                  {usedVehicleGlyphs.map(({ key, icon: Icon, color }) => (
                                    <span className="legend-item" key={key}>
                                      <span className="legend-mark" style={{ borderColor: color, color }}>
                                        <Icon size={9} strokeWidth={2.4} />
                                      </span>
                                      {t.vehicleGlyphName[key]}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>

                            {/*
                              Trafikkmeldingene står over dagsplanen, ikke i den: en stengt vei
                              ligger mellom to stopp og rammer dagen man skal kjøre den. Panelet
                              forsvinner helt når det er klar vei.
                            */}
                            {/*
                              Veimeldingene, sammenleggbare — men bare når de ikke haster.
                            
                              Meldt fra bruk: panelet er irriterende. Det er det fordi det oftest er
                              fullt av veiarbeid — 974 av 1247 meldinger i Norge — og et panel man må
                              bla forbi hver gang lærer man seg å overse. Nå ligger det sammenlagt med
                              tellingen i hodet, så det å legge sammen skjuler utledningen og ikke
                              svaret.
                            
                              Unntaket er det som betyr noe: er en vei stengt eller kolonnekjørt, eller
                              finnes det et naturfarevarsel, står panelet åpent — også på telefon. At
                              Vikafjellet er stengt i morgen tidlig er ikke et oppslag man går til når
                              man er nysgjerrig; det er noe som endrer turen, og et panel som skjuler
                              det bak et trykk lar noen kjøre av gårde uten å vite det.
                            */}
                            {/*
                              Høydeprofilen står i hovedspalten, ikke i sidespalten.
                            
                              Den er en SVG med fast bredde og et viewBox — en rutes høydeprofil *er*
                              bred — og i en spalte på 340 piksler ble den presset til noe man ikke
                              kunne lese. Den hører dessuten hjemme her: den beskriver strekningen,
                              som er det hovedspalten handler om.
                            */}
                            <FoldPanel
                              id="terrain"
                              title={t.terrainTitle}
                              icon={<MountainSnow size={14} />}
                              openOnWide={false}
                            >
                              <p className="card-sub">{t.terrainSubtitle}</p>
                              <TerrainProfile
                                stops={routeStops}
                                activeIds={activeIds}
                                lang={lang}
                                onSelect={setSelectedStop}
                                onHover={setHoveredStop}
                              />
                            </FoldPanel>
                            {(trafficAlerts.length > 0 || trafficHazards.length > 0) && (
                              <FoldPanel
                                id="traffic"
                                title={t.trafficTitle}
                                icon={<AlertTriangle size={14} />}
                                summary={
                                  trafficBlocking > 0
                                    ? t.trafficBlocking(trafficBlocking)
                                    : t.trafficCount(trafficAlerts.length)
                                }
                                openOnNarrow={trafficBlocking > 0}
                              >
                                <TrafficAlertsPanel
                                  bare
                                  alerts={trafficAlerts}
                                  hazards={trafficHazards}
                                  staleSince={restoredAt}
                                  lang={lang}
                                  onSelectStop={(stopId) => {
                                    const stop = allStops.find((s) => s.id === stopId);
                                    if (stop) setSelectedStop(stop);
                                  }}
                                />
                              </FoldPanel>
                            )}


                            {/* Ferjetidene står før dagsplanen: de er tider dagen må legges rundt. */}
                            <FerryPanel
                              crossings={itinerary.ferries ?? []}
                              staleSince={restoredAt}
                              lang={lang}
                            />


                            {/*
                              Reisen i tall, som et kort man kan dele.

                              Står mellom planen og nødnumrene: det er slutten på det man leser
                              med glede, og begynnelsen på det man leser med alvor.
                            */}
                            

                            {/*
                              Nødnumrene, og hva man skal si.

                              De står under planen, ikke over den: man planlegger først og leser
                              dette etterpå, eller aldri. Men de står på *første* side i
                              utskriften, fordi papiret er det man har når telefonen er død — og
                              det er nettopp da et nødnummer er vanskeligst å finne.
                            */}
                            <div className="card no-print" id="emergency">
                              <EmergencyCard
                                stops={itinerary.days.flatMap((day) => day.stops)}
                                lang={lang}
                              />
                            </div>

                            {/*
                              Reiseplanen for papir. Skjermen viser én dag om gangen bak faner,
                              som er riktig når man planlegger — men en utskrift av dag 3 er ikke
                              en reiseplan. Roadbook-en har alle dagene, formatert for A4.
                            */}
                            <Roadbook itinerary={itinerary} lang={lang} />
              </div>

              <aside className="plan-side" aria-label={t.planSideLabel}>

                <FoldPanel
                  id="cost"
                  title={t.foldCost}
                  icon={<Wallet size={14} />}
                  summary={costSummary}
                >
                              {itinerary.budget && <BudgetPanel budget={itinerary.budget} lang={lang} />}

                              {/*
                                Klesrådet sto bare i den utskrevne roadbooken. De fleste åpner
                                aldri utskriftsversjonen før de faktisk reiser — så en plan som
                                sto ferdig på skjermen aldri fortalte at Sognefjellet i februar
                                betyr vinterjakke og brodder. Se components/PackingPanel.tsx.
                              */}
                              {itinerary.packing && <PackingPanel packing={itinerary.packing} lang={lang} />}
                </FoldPanel>

                {/*
                  Billettene står rett under kostnaden, og rekkefølgen er et
                  argument: panelet over sier hva turen koster, dette sier hvor
                  man betaler det. Se components/TravelServices.tsx.
                */}
                <FoldPanel
                  id="services"
                  title={t.servicesTitle}
                  icon={<CreditCard size={14} />}
                >
                  <TravelServices
                    mode={itinerary.request.mode}
                    startName={itinerary.days[0]?.stops[0]?.name ?? ""}
                    vehicle={itinerary.request.vehicle}
                    lang={lang}
                  />
                </FoldPanel>

                <FoldPanel
                  id="share"
                  title={t.foldShare}
                  icon={<Share2 size={14} />}
                  openOnWide={false}
                >
                              {/*
                                Å dele turen.

                                Står etter budsjettet med vilje, og rekkefølgen er et argument:
                                det som gjør delingen verdt noe er tallet rett over — hva turen
                                koster per person når flere blir med. Panelet over sier at bilen
                                er den delen som blir billigere av selskap; dette er knappen som
                                gjør noe med det.

                                Lenka bygges fra det samme skjemaet adresselinjen speiler, så det
                                finnes bare én definisjon av hva en delt tur er.
                                Se components/ShareTrip.tsx og lib/url.ts.
                              */}
                              <ShareTrip
                                url={shareUrl(form, lang)}
                                ownerName={form.ownerName}
                                onNameChange={(ownerName) => {
                                  /*
                                   * Navnet endrer ikke ruta, så planen skal ikke hentes på nytt.
                                   * `adjust` ville sendt en ny forespørsel til serveren for en
                                   * opplysning serveren ikke bruker — og lagt på et sekund
                                   * ventetid per bokstav.
                                   */
                                  setForm((current) => {
                                    const next = { ...current, ownerName };
                                    syncUrl(next, lang);
                                    return next;
                                  });
                                }}
                                sharedBy={sharedBy}
                                lang={lang}
                              />

                              {/*
                                Lyttetipsene står etter budsjettet: begge er ting man leser mens
                                man planlegger, ikke mens man kjører. Panelet skjuler seg selv når
                                ruta ikke har en liste.
                              */}
                              <SoundtrackPanel
                                soundtrack={itinerary.soundtrack}
                                travelHours={itinerary.totalTravelHours}
                                lang={lang}
                              />
                </FoldPanel>
              </aside>
            </div>
          </>
        )}

        {!itinerary && !loading && (
          <div className="empty no-print">
            <Compass size={30} color="#8A9A9F" />
            <h3>{t.emptyTitle}</h3>
            <p>{t.emptyBody}</p>
          </div>
        )}

        {loading && !itinerary && (
          <div className="card no-print" aria-busy="true">
            <div className="skeleton" style={{ height: 26, width: "45%", marginBottom: 14 }} />
            <div className="skeleton" style={{ height: 15, width: "80%", marginBottom: 10 }} />
            <div className="skeleton" style={{ height: 15, width: "62%", marginBottom: 24 }} />
            <div className="skeleton" style={{ height: 190 }} />
          </div>
        )}

        {/*
          Om siden, rett over bunnteksten.

          Ikke i bunnteksten: den er kilder og forbehold, og et skjema der ville
          lest som en fotnote. Ikke i toppen heller — den som nettopp har
          kommet inn skal planlegge en tur, ikke lese om hvem som lagde siden.
          Her står den der man er når man har sett det man kom for.
        */}
        <AboutSection lang={lang} contactEnabled={contactEnabled} />

        <footer className="footer">
          {/*
            Lenker ned til sidene som kan indekseres.

            Innholdet i appen har 130 egne adresser — én per sted og én per rute,
            servert som ekte HTML av Worker-en, se api/src/services/seo/. Uten en
            lenke herfra er de sider bare et sitemap kjenner, og en side ingen
            lenker til teller mindre enn den fortjener. De er også nyttige i seg
            selv: den som vil lese om ett sted skal slippe å planlegge en tur
            først.

            Ekte `<a href>` og ikke en knapp med `onClick`: en søkemotor følger
            lenker, ikke hendelseslyttere.
          */}
          <nav className="footer-browse" aria-label={t.browseTitle}>
            <a href={langHref("/trips")}>{t.browseTrips}</a>
            <a href={langHref("/places")}>{t.browsePlaces}</a>
          </nav>
          <p style={{ margin: 0 }}>
            <Route size={12} style={{ verticalAlign: -1, marginRight: 6 }} />
            {t.footerSources}
          </p>
          <p style={{ margin: "8px 0 0" }}>{t.footerDisclaimer}</p>
        </footer>

        {/*
          Baren nederst til høyre: hva appen kan bevise, og et sted å si noe
          selv. Utenfor <main>, fordi den ikke er en del av innholdet — den
          ligger over det. Se components/FeedbackDock.tsx.
        */}
        <FeedbackDock lang={lang} contactEnabled={contactEnabled} />

        {/*
          Skuffen har sin egen grense.

          Det var her det faktisk krasjet: `endpoints` sendte et raatt stopp,
          skuffen leste `stop.producers.length`, og uten en grense tok den med
          seg hele planen. Kilden er rettet, men en skuff som feiler skal koste
          en skuff — ikke turen.
        */}
        {selectedStop && (
          <ErrorBoundary label={t.partPanels} lang={lang} resetKeys={[selectedStop.id]}>
          <StopDrawer
            stop={selectedStop}
            daylight={itinerary?.days[activeDay]?.daylight}
            day={itinerary?.days[activeDay]}
            lang={lang}
            placeKinds={placeKinds}
            /* Klimanormalen er tolv tall; uten måneden svarer de på ingenting. */
            month={form.startDate ? Number(form.startDate.slice(5, 7)) : undefined}
            onClose={() => setSelectedStop(null)}
          />
          </ErrorBoundary>
        )}

        {/* Bildekortet vises bare når ingen detaljvisning er åpen. */}
        {/*
          Presentasjonen av ruta. Åpnes av et klikk i rutevelgeren, og viser
          stoppene etter hvert som de kommer inn — den venter ikke på alt.
        */}
        {showcaseId &&
          (() => {
            const corridor = corridors.find((c) => c.id === showcaseId);
            if (!corridor) return null;
            return (
              <CorridorShowcase
                corridor={corridor}
                stops={showcaseId === form.corridorId ? allStops : []}
                lang={lang}
                onClose={() => setShowcaseId(null)}
                onPlan={() => {
                  setShowcaseId(null);
                  void generate({ ...form, corridorId: showcaseId });
                  // Samme ønske som de andre veiene inn. Effekten venter til
                  // presentasjonen er lukket og planen er kommet.
                  setWants("form");
                }}
              />
            );
          })()}

        {!selectedStop && (
          <StopHoverCard
            stop={hoveredStop}
            lang={lang}
            onDismiss={() => setHoveredStop(null)}
          />
        )}
        </div>
      </div>
    </>
  );
}
